import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Calculator, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArtificialGrassCalculator() {
  const [size, setSize] = useState(40);
  const [quality, setQuality] = useState("standard");
  const [groundwork, setGroundwork] = useState("standard");
  const [access, setAccess] = useState("good");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const estimate = useMemo(() => {
    // Base £/m² by grass quality
    const grassRate = { budget: 18, standard: 26, premium: 34 }[quality];
    // Install rate £/m² by groundwork condition
    const installRate = { standard: 45, heavy: 60, light: 35 }[groundwork];
    // Access multiplier
    const accessMult = { good: 1.0, moderate: 1.08, difficult: 1.18 }[access];
    const perSqm = (grassRate + installRate) * accessMult;
    const low = Math.round(size * perSqm * 0.92);
    const high = Math.round(size * perSqm * 1.10);
    return { low, high, perSqm: Math.round(perSqm) };
  }, [size, quality, groundwork, access]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone) {
      toast.error("Please add name, email and phone to receive your quote");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        name, email, phone, postcode,
        service: "Artificial Grass Installation",
        location: "",
        message: `Calculator: ${size}m² · ${quality} grass · ${groundwork} groundwork · ${access} access · Estimate £${estimate.low.toLocaleString()}–£${estimate.high.toLocaleString()}`,
        source_page: "/services/artificial-grass#calculator",
      });
      setSent(true);
      toast.success("Estimate sent — full quote within 24 hours");
    } catch {
      toast.error("Could not send — please call us directly");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-16 lg:py-24" data-testid="grass-calculator">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="pill mb-4"><Calculator size={12}/> Instant estimate</div>
            <h2 className="font-display text-4xl lg:text-5xl text-dark">Get a price <span className="italic text-[color:var(--lc-green)]">in seconds.</span></h2>
            <p className="text-dark/70 text-lg mt-5">Drag the sliders, pick your spec, and see your installed-price range live. No signup, no waiting. Send it to us and we'll confirm a full quote within 24 hours.</p>
            <ul className="mt-6 space-y-2 text-sm text-dark/65">
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[color:var(--lc-lime)]"/> 2026 Scottish pricing</li>
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[color:var(--lc-lime)]"/> Includes ground preparation</li>
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[color:var(--lc-lime)]"/> 10-year manufacturer warranty</li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="lc-card p-6 lg:p-8">
              {/* Size */}
              <div className="mb-6">
                <div className="flex items-baseline justify-between">
                  <label className="font-semibold text-dark text-sm uppercase tracking-wider">Garden size</label>
                  <span className="font-display text-3xl text-dark">{size}<span className="text-base text-dark/50"> m²</span></span>
                </div>
                <input type="range" min="5" max="300" step="5" value={size} onChange={(e)=>setSize(Number(e.target.value))} className="w-full mt-3 accent-[color:var(--lc-dark)]" data-testid="calc-size" />
                <div className="flex justify-between text-xs text-dark/50 mt-1"><span>5m²</span><span>300m²</span></div>
              </div>

              {/* Quality */}
              <Group label="Grass quality">
                <Pill active={quality==="budget"} onClick={()=>setQuality("budget")} testid="calc-q-budget">Budget · 25mm</Pill>
                <Pill active={quality==="standard"} onClick={()=>setQuality("standard")} testid="calc-q-standard">Standard · 32mm</Pill>
                <Pill active={quality==="premium"} onClick={()=>setQuality("premium")} testid="calc-q-premium">Premium · 40mm</Pill>
              </Group>

              <Group label="Ground condition">
                <Pill active={groundwork==="light"} onClick={()=>setGroundwork("light")} testid="calc-g-light">Existing patio/hard</Pill>
                <Pill active={groundwork==="standard"} onClick={()=>setGroundwork("standard")} testid="calc-g-std">Existing lawn</Pill>
                <Pill active={groundwork==="heavy"} onClick={()=>setGroundwork("heavy")} testid="calc-g-heavy">Mud / waterlogged</Pill>
              </Group>

              <Group label="Access">
                <Pill active={access==="good"} onClick={()=>setAccess("good")} testid="calc-a-good">Drive / side gate</Pill>
                <Pill active={access==="moderate"} onClick={()=>setAccess("moderate")} testid="calc-a-mod">Through house</Pill>
                <Pill active={access==="difficult"} onClick={()=>setAccess("difficult")} testid="calc-a-diff">Tight / restricted</Pill>
              </Group>

              {/* Estimate */}
              <div className="mt-6 p-5 rounded-2xl bg-dark text-white relative noise overflow-hidden" data-testid="calc-estimate">
                <div className="text-xs uppercase tracking-[0.18em] text-lime/90 font-semibold">Your estimate</div>
                <div className="font-display text-5xl lg:text-6xl mt-1">
                  £{estimate.low.toLocaleString()}<span className="text-white/50 text-2xl"> – </span>£{estimate.high.toLocaleString()}
                </div>
                <div className="text-sm text-white/70 mt-2">~£{estimate.perSqm}/m² fully fitted · {size}m² · {quality} grass</div>
              </div>

              {!sent ? (
                <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3">
                  <input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Full name *" data-testid="calc-name" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30"/>
                  <input value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="Phone *" type="tel" data-testid="calc-phone" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30"/>
                  <input value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Email *" type="email" data-testid="calc-email" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30"/>
                  <input value={postcode} onChange={(e)=>setPostcode(e.target.value)} placeholder="Postcode" data-testid="calc-postcode" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30"/>
                  <button type="submit" disabled={sending} className="btn-primary sm:col-span-2 mt-1" data-testid="calc-submit">
                    {sending ? <Loader2 className="animate-spin" size={16}/> : <ArrowRight size={16}/>} {sending ? "Sending…" : "Lock in my estimate & get a full quote"}
                  </button>
                </form>
              ) : (
                <div className="mt-5 p-5 rounded-xl bg-paper text-dark text-center" data-testid="calc-success">
                  <div className="font-display text-2xl">Thanks — your estimate is on the way.</div>
                  <div className="text-sm text-dark/65 mt-1">A specialist will be in touch within 24 hours with your detailed quote.</div>
                  <Link to="/projects" className="btn-ghost mt-4 inline-flex">See our recent installs</Link>
                </div>
              )}

              <p className="text-xs text-dark/55 mt-3">Estimate is indicative only. Final price subject to site survey. Includes excavation, sub-base, weed membrane, premium grass and edge fixing.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Group = ({ label, children }) => (
  <div className="mb-5">
    <label className="font-semibold text-dark text-sm uppercase tracking-wider">{label}</label>
    <div className="flex flex-wrap gap-2 mt-2">{children}</div>
  </div>
);

const Pill = ({ active, testid, ...p }) => (
  <button type="button" {...p} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${active ? "bg-dark text-white border-dark" : "bg-white text-dark border-lc hover:border-dark"}`} />
);
